import { useEffect, useRef, useState } from 'react'
import type {
    AdjuntoDTO,
    CRMTextoDTO,
    CRMTextoTipo,
    PagaebleResult,
    PolizaDTO,
    ReciboDTO,
    SearchSiniestroDTO,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import {
    canRenewPolicy,
    CONDUCTO_LABELS,
    createPolicyNote,
    currencyCode,
    deletePolicyAdjunto,
    errorMessage,
    fetchPolicyAdjuntos,
    fetchPolicyAndReciboAdjuntos,
    fetchPolicyBitacora,
    findCaratula,
    isOverdueRecibo,
    isPaidRecibo,
    isPendingRecibo,
    labelize,
    NEW_POLICY_PATH,
    nextPendingRecibo,
    NOTE_TYPES,
    noteMeta,
    personName,
    POLICIES_PATH,
    policyCommission,
    reciboCommission,
    reciboIdsOf,
    reciboTag,
    renewBlockedMessage,
    restanText,
    statusKey,
    statusView,
    sumComision,
    sumTotal,
    uploadPolicyFiles,
    vigenciaView,
} from '../common/policy-common'
import type { BitacoraFilter, LoadState, PolicyFormLocationState } from '../common/policy-common'

export type PolicyDrawerTab = 'general' | 'recibos' | 'siniestros' | 'bitacora' | 'adjuntos'

export interface PolicyDrawerProps {
    polizaId: number | null
    onClose: () => void
    onClosed?: (poliza?: PolizaDTO | null) => void
    initialTab?: PolicyDrawerTab
    /** A dónde regresa el formulario al renovar desde el drawer. */
    backUrl?: string
}

export interface PolicyDrawerTabItem {
    id: PolicyDrawerTab
    label: string
    count?: number | null
}

const DETAIL_ERROR = 'No se pudo cargar el detalle de la póliza.'
const SINIESTROS_ERROR = 'No se pudieron cargar los siniestros.'
const BITACORA_ERROR = 'No se pudo cargar la bitácora.'
const ADJUNTOS_ERROR = 'No se pudieron cargar los adjuntos.'
const SAVE_NOTE_ERROR = 'No se pudo guardar la bitácora.'
const UPLOAD_ERROR = 'No se pudo subir el archivo.'

export { extTone, fileExt, reciboChip, reciboPaidOn } from '../common/policy-common'
export { siniestroChip } from '../../claims/common/claim-common'

const paymentChip = (poliza: PolizaDTO, recibos: ReciboDTO[]) => {
    if (statusView(poliza.status, poliza.fechaVigenciaDe, poliza.fechaVigenciaA).tone === 'closed') {
        return { label: 'Sin recibos activos', className: 'bg-k-line text-k-ink-2' }
    }
    if (recibos.some(isOverdueRecibo)) return { label: 'Recibo vencido', className: 'bg-k-danger-soft text-k-danger-ink' }
    return { label: 'Pagos al corriente', className: 'bg-k-ok-soft text-k-ok' }
}

export { aseguradoraName, vehicleLabel } from '../common/policy-common'

export const usePolicyDrawerModel = ({ polizaId, onClose, onClosed, initialTab = 'general', backUrl = POLICIES_PATH }: PolicyDrawerProps) => {
    const history = useHistory()
    const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const bitacoraLoadedFor = useRef<number | null>(null)
    const adjuntosLoadedFor = useRef<number | null>(null)
    const siniestrosLoadedFor = useRef<number | null>(null)
    const [poliza, setPoliza] = useState<PolizaDTO | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [reloadToken, setReloadToken] = useState(0)
    const [tab, setTab] = useState<PolicyDrawerTab>('general')
    const [toast, setToast] = useState<string | null>(null)
    const [closing, setClosing] = useState(false)
    const [siniestros, setSiniestros] = useState<SearchSiniestroDTO[]>([])
    const [siniestrosState, setSiniestrosState] = useState<LoadState>('idle')
    const [siniestrosError, setSiniestrosError] = useState<string | null>(null)
    const [bitacora, setBitacora] = useState<CRMTextoDTO[]>([])
    const [bitacoraState, setBitacoraState] = useState<LoadState>('idle')
    const [bitacoraError, setBitacoraError] = useState<string | null>(null)
    const [noteType, setNoteType] = useState<CRMTextoTipo>(NOTE_TYPES[0].id)
    const [note, setNote] = useState('')
    const [savingNote, setSavingNote] = useState(false)
    const [bitacoraFilter, setBitacoraFilter] = useState<BitacoraFilter>('todo')
    const [adjuntos, setAdjuntos] = useState<AdjuntoDTO[]>([])
    const [adjuntosState, setAdjuntosState] = useState<LoadState>('idle')
    const [adjuntosError, setAdjuntosError] = useState<string | null>(null)
    const [uploading, setUploading] = useState(false)
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

    const showToast = (message: string) => {
        clearTimeout(toastTimer.current)
        setToast(message)
        toastTimer.current = setTimeout(() => setToast(null), 3000)
    }

    useEffect(() => () => clearTimeout(toastTimer.current), [])

    useEffect(() => {
        if (!polizaId) return undefined
        setTab(initialTab)
        setPoliza(null)
        setSiniestros([])
        setSiniestrosState('idle')
        setSiniestrosError(null)
        siniestrosLoadedFor.current = null
        setBitacora([])
        setBitacoraState('idle')
        setBitacoraError(null)
        bitacoraLoadedFor.current = null
        setNote('')
        setBitacoraFilter('todo')
        setAdjuntos([])
        setAdjuntosState('idle')
        setAdjuntosError(null)
        adjuntosLoadedFor.current = null
        setConfirmDeleteId(null)
        setClosing(false)
        return undefined
    }, [polizaId, initialTab])

    useEffect(() => {
        if (!polizaId) return undefined
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const response = await APIInvoke.invokeGET(`/polizas/${polizaId}`)
                const payload: WrapperResponse<PolizaDTO> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || DETAIL_ERROR)
                setPoliza(payload.body)
            } catch (loadError) {
                if (active) {
                    setPoliza(null)
                    setError(errorMessage(loadError, DETAIL_ERROR))
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [polizaId, reloadToken])

    useEffect(() => {
        if (!polizaId || (tab !== 'general' && tab !== 'siniestros') || siniestrosLoadedFor.current === polizaId) return undefined
        let active = true
        const load = async () => {
            try {
                setSiniestrosState('loading')
                setSiniestrosError(null)
                const response = await APIInvoke.invokeGET(`/siniestros?polizaId=${polizaId}&size=20`)
                const payload: WrapperResponse<PagaebleResult<SearchSiniestroDTO>> | null = await response.json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || SINIESTROS_ERROR)
                setSiniestros(payload.body?.items || [])
                setSiniestrosState('ready')
                siniestrosLoadedFor.current = polizaId
            } catch (loadError) {
                if (active) {
                    setSiniestrosState('error')
                    setSiniestrosError(errorMessage(loadError, SINIESTROS_ERROR))
                }
            }
        }
        load()
        return () => { active = false }
    }, [polizaId, tab])

    useEffect(() => {
        if (!polizaId || tab !== 'bitacora' || bitacoraLoadedFor.current === polizaId) return undefined
        let active = true
        const load = async () => {
            try {
                setBitacoraState('loading')
                setBitacoraError(null)
                const textos = await fetchPolicyBitacora(polizaId)
                if (!active) return
                setBitacora(textos)
                setBitacoraState('ready')
                bitacoraLoadedFor.current = polizaId
            } catch (loadError) {
                if (active) {
                    setBitacoraState('error')
                    setBitacoraError(errorMessage(loadError, BITACORA_ERROR))
                }
            }
        }
        load()
        return () => { active = false }
    }, [polizaId, tab])

    useEffect(() => {
        if (!poliza || poliza.id !== polizaId || tab !== 'adjuntos' || adjuntosLoadedFor.current === polizaId) return undefined
        let active = true
        const load = async () => {
            try {
                setAdjuntosState('loading')
                setAdjuntosError(null)
                const items = await fetchPolicyAndReciboAdjuntos(poliza.id, reciboIdsOf(poliza))
                if (!active) return
                setAdjuntos(items)
                setAdjuntosState('ready')
                adjuntosLoadedFor.current = polizaId
            } catch (loadError) {
                if (active) {
                    setAdjuntosState('error')
                    setAdjuntosError(errorMessage(loadError, ADJUNTOS_ERROR))
                }
            }
        }
        load()
        return () => { active = false }
    }, [poliza, polizaId, tab])

    const copyNumber = async () => {
        if (!poliza?.noPoliza) return
        try {
            await navigator.clipboard.writeText(poliza.noPoliza)
            showToast('Número de póliza copiado')
        } catch {
            showToast('No se pudo copiar el número')
        }
    }

    const openFile = async (path?: string | null) => {
        try {
            const url: string | null | undefined = await APIInvoke.resolveStoredFileUrl(path)
            if (url) window.open(url, '_blank', 'noopener')
        } catch (fileError) {
            showToast(errorMessage(fileError, 'No se pudo abrir el archivo.'))
        }
    }

    const canRenew = canRenewPolicy(poliza)

    const renewPolicy = () => {
        if (!polizaId) return
        if (!canRenew) {
            showToast(renewBlockedMessage(poliza))
            return
        }
        onClose()
        const state: PolicyFormLocationState = { polizaId, action: 'renovar', backUrl }
        history.push(NEW_POLICY_PATH, state)
    }

    const openCaratula = async () => {
        if (!polizaId) return
        let files = adjuntos.filter((item) => item.reciboId == null)
        if (adjuntosLoadedFor.current !== polizaId) {
            try {
                files = await fetchPolicyAdjuntos(polizaId)
            } catch (loadError) {
                showToast(errorMessage(loadError, 'No se pudo abrir la carátula.'))
                return
            }
        }
        const caratula = findCaratula(files)
        if (!caratula?.path) {
            showToast('Esta póliza no tiene carátula adjunta.')
            return
        }
        openFile(caratula.path)
    }

    const addNote = async () => {
        const texto = note.trim()
        if (!texto || !poliza) return
        try {
            setSavingNote(true)
            const created = await createPolicyNote(poliza, noteType, texto)
            if (created) setBitacora((items) => [created, ...items])
            setNote('')
            setBitacoraFilter('todo')
            showToast('Nota agregada a la bitácora')
        } catch (saveError) {
            showToast(errorMessage(saveError, SAVE_NOTE_ERROR))
        } finally {
            setSavingNote(false)
        }
    }

    const uploadFiles = async (fileList: FileList | null) => {
        const files = Array.from(fileList || [])
        if (!files.length || !poliza) return
        try {
            setUploading(true)
            const created = await uploadPolicyFiles(poliza.id, files)
            setAdjuntos((items) => [...created, ...items])
            showToast(files.length === 1 ? 'Archivo subido' : `${files.length} archivos subidos`)
        } catch (uploadError) {
            showToast(errorMessage(uploadError, UPLOAD_ERROR))
        } finally {
            setUploading(false)
        }
    }

    const deleteAdjunto = async (id: number) => {
        try {
            await deletePolicyAdjunto(id)
            setAdjuntos((items) => items.filter((item) => item.id !== id))
            setConfirmDeleteId(null)
            showToast('Archivo eliminado')
        } catch (deleteError) {
            showToast(errorMessage(deleteError, 'No se pudo eliminar el archivo.'))
        }
    }

    const closeCompleted = (closed?: PolizaDTO | null) => {
        setClosing(false)
        setReloadToken((token) => token + 1)
        showToast(`Póliza ${closed?.noPoliza || poliza?.noPoliza || ''} cerrada`)
        onClosed?.(closed)
    }

    const cliente = poliza?.cliente
    const name = cliente ? personName(cliente) : ''
    const recibos = [...(poliza?.recibos || [])]
        .sort((left, right) => Number(right.serie || 0) - Number(left.serie || 0))
        .map((recibo) => ({ ...recibo, comision: reciboCommission(recibo, poliza) }))
    const paid = recibos.filter(isPaidRecibo)
    const pending = recibos.filter(isPendingRecibo)
    const overdue = recibos.filter(isOverdueRecibo)
    const commission = policyCommission(poliza)
    const notas = bitacora.filter((item) => statusKey(item.tipo) !== 'adjunto')
    const tabs: PolicyDrawerTabItem[] = [
        { id: 'general', label: 'Datos generales' },
        { id: 'recibos', label: 'Recibos', count: poliza ? recibos.length : null },
        { id: 'siniestros', label: 'Siniestros', count: siniestrosState === 'ready' ? siniestros.length : null },
        { id: 'bitacora', label: 'Bitácora', count: bitacoraState === 'ready' ? notas.length : null },
        { id: 'adjuntos', label: 'Adjuntos', count: adjuntosState === 'ready' ? adjuntos.length : null },
    ]

    return {
        poliza,
        loading,
        error,
        tab,
        tabs,
        toast,
        closing,
        cliente,
        name,
        currency: currencyCode(poliza?.moneda),
        recibos,
        paidCount: paid.length,
        pendingTotal: sumTotal(pending),
        comisionPct: commission.pct,
        comisionTotal: commission.monto,
        comisionCobrada: sumComision(paid),
        comisionPorCobrar: sumComision(pending),
        overdueCount: overdue.length,
        overdueTotal: sumTotal(overdue),
        nextReceipt: nextPendingRecibo(recibos),
        status: poliza ? statusView(poliza.status, poliza.fechaVigenciaDe, poliza.fechaVigenciaA) : null,
        vigencia: poliza ? vigenciaView(poliza.fechaVigenciaDe, poliza.fechaVigenciaA, poliza.status) : null,
        restan: poliza ? restanText(poliza.status, poliza.fechaVigenciaA) : null,
        pago: poliza ? paymentChip(poliza, recibos) : null,
        forma: labelize(poliza?.formaPago),
        conducto: poliza?.conductoDescripcion || (poliza?.conducto ? CONDUCTO_LABELS[poliza.conducto] : null) || labelize(poliza?.conducto),
        asegurados: poliza?.asegurados?.length ? poliza.asegurados.map((item) => personName(item)).filter(Boolean).join(', ') : name,
        canRenew,
        siniestros,
        siniestrosState,
        siniestrosError,
        bitacoraState,
        bitacoraError,
        bitacoraFilter,
        visibleNotes: notas.filter((item) => bitacoraFilter === 'todo' || noteMeta(item.tipo).filter === bitacoraFilter),
        noteType,
        note,
        notePlaceholder: NOTE_TYPES.find((item) => item.id === noteType)?.placeholder,
        savingNote,
        adjuntos: adjuntos.map((adjunto) => ({
            adjunto,
            tag: adjunto.reciboId != null ? reciboTag(recibos, adjunto.reciboId) : 'Póliza',
            fromRecibo: adjunto.reciboId != null,
        })),
        adjuntosState,
        adjuntosError,
        uploading,
        confirmDeleteId,
        setTab,
        setNoteType,
        setNote,
        setBitacoraFilter,
        setConfirmDeleteId,
        reload: () => setReloadToken((token) => token + 1),
        openClose: () => setClosing(true),
        cancelClose: () => setClosing(false),
        closeCompleted,
        copyNumber,
        openFile,
        openCaratula,
        renewPolicy,
        addNote,
        uploadFiles,
        deleteAdjunto,
    }
}
